import React, { useCallback, useState, useEffect } from 'react';
import { injectIntl, FormattedMessage } from 'react-intl';
import { useLazyQuery, useMutation } from '@apollo/react-hooks';
import { Button, Responsive, Icon, Dropdown, Popup, Checkbox } from 'semantic-ui-react';
import { useRouteMatch, matchPath } from 'react-router-dom';
import { differenceWith, isEqual } from 'lodash';
import moment from 'moment-timezone';
import DeleteIcon from '@uniiverse/library/assets/icons/trash.svg';
import { ErrorFlash } from 'components/create/error-flash/ErrorFlash';
import { blockInvalidChar } from 'lib/block-invalid-char';
import { breakpoints } from 'helpers/breakpoints';
import MediaQuery from 'react-responsive';
import getSymbolFromCurrency from 'currency-symbol-map';
import { DescriptionInput as AddOnDescriptionInput,
  DescriptionInput as PostPurchaseMessageInput } from './DescriptionInput';
import CreateButtons from './create-buttons';
import EditButton from './edit-button';
import BlueMinusSign from '../../../assets/add-ons/minus_blue.svg';
import Exclamation from '../../../assets/add-ons/exclamation.svg';
import ErrorIcon from '../../../assets/info-error.svg';
import ToolTipIcon from '../../../assets/add-ons/info.svg';
import { fields } from './consts';
import { addons } from './locale';
import { Input } from '../../inputs';
import ImageUpload from '../../create/image-uploader/image-upload';
import Tooltip from '../../tooltip/tooltip';
import style from './index-module.css';
import CREATE_TAX_MUTATION from '../create-tax.gql';
import TaxForm from './tax-form';
import getHost from '../get-host.gql';
import getHostAndAddOn from '../get-host-and-add-on.gql';
import ListingsSelector from '../listings-selector';
import { getCharacterCountWithoutHTML } from '../../../lib/rich-text-editor-helper';
import AddOnsCapacity from '../limit-capacity';
import { INACTIVE, ACTIVE, SELECT_ALL_LISTINGS,
  SELECT_SOME_LISTINGS, } from '../consts';
import ListingTimeslot from '../listings-timeslot';

const MAX_TITLE_LENGTH = '95';
const MAX_DESCRIPTION_LENGTH = '400';
const SMALL_DEVICE_BREAK_POINT = 767;
const EMPTY_TAX_RATE_ID = 'empty_tax_rate';
const PLACEHOLDER_TAX_RATE_ID = 'placeholder_tax_rate';
const CUSTOM_TAX_SELECTION_ID = 'new_custom_tax';
const MAX_PRICE_ALLOWED = 999999999;
const FILTER_TAX_IDS = [CUSTOM_TAX_SELECTION_ID, PLACEHOLDER_TAX_RATE_ID, EMPTY_TAX_RATE_ID];

const AddOnsCreateEditForm = injectIntl((props) => {
  const [serverErrors, setServerErrors] = useState([]);
  const [name, setName] = useState('');
  const [currencies, setCurrencies] = useState([]);
  const firstCurrency = currencies?.[0];
  const [currency, setCurrency] = useState(firstCurrency ? firstCurrency?.id : '');
  const [data, setData] = useState({});
  const [description, setDescription] = useState('');
  const [hostPaysCommission, setHostPaysCommission] = useState(true);
  const [availableTaxes, setAvailableTaxes] = useState([]);
  const [taxes, setTaxes] = useState([]);
  const [imageUrl, setImageUrl] = useState(null);
  const [images, setImages] = useState([]);
  const [addImage, setAddImage] = useState(undefined);
  const [selectedTaxes, setSelectedTaxes] = useState([]);
  const [savingList, setSavingList] = useState({});
  const [rates, setRates] = useState([]);
  const [forceHostToPayCommission, setForceHostToPayCommission] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isDeleted, setIsDeleted] = useState(false);
  const [createTax] = useMutation(CREATE_TAX_MUTATION);
  const [listingIds, setListingIds] = useState([]);
  const [selectedTicketTypes, setSelectedTicketTypes] = useState({});
  const [clientErrors, setClientErrors] = useState({
    name: {
      blank: null,
      characterLength: null
    },
    price: {
      blank: null
    },
    description: {
      characterLength: null
    },
    postPurchaseMessage: {
      characterLength: null
    },
    optionNames: {
      blank: null
    },
    rates: {}
  });
  const [postPurchaseMessage, setPostPurchaseMessage] = useState('');
  const [showPostPurchaseMessage, setShowPostPurchaseMessage] = useState(false);
  const [addOnCapacity, setAddOnCapacity] = useState([]);
  const [capacityLimitCustomization, setCapacityLimitCustomization] = useState([]);
  const [deletedRateIndex, setDeletedRateIndex] = useState(null);
  const [toggle, setToggle] = useState(INACTIVE);
  const [eventData, setEventData] = useState([]);
  const [dataLoader, setDataLoader] = useState(true);
  const [timeslotList, setTimeslotList] = useState([]);
  const [addOnAvailabilityRule, setAddOnAvailabilityRule] = useState([]);
  const initialListingFilterState = listingIds.length >= 1 ? SELECT_SOME_LISTINGS : SELECT_ALL_LISTINGS;
  const [listingsFilterSetting, setListingsFilterSetting] = useState(initialListingFilterState);
  const [hideQRCode, setHideQRCode] = useState(false);

  const elements = {
    rows: {},
    basicInfoHeader: null
  };

  const {
    params: { id: editId }
  } = useRouteMatch();

  const [fetchHostData, { loading: hostDataLoading }] = useLazyQuery(getHost, {
    variables: {
      hostId: props.hostId,
    },
    fetchPolicy: 'no-cache',
    onCompleted: (response) => addOnHostQueryResponse(response),
  });

  const [fetchHostAddOnData, { loading: hostAddonDataLoading }] = useLazyQuery(getHostAndAddOn, {
    variables: {
      hostId: props.hostId,
      addOnId: editId,
    },
    fetchPolicy: 'no-cache',
    onCompleted: (response) => addOnHostQueryResponse(response),
  });

  useEffect(() => {
    editId ? fetchHostAddOnData() : fetchHostData();
  }, [editId, fetchHostAddOnData, fetchHostData]);

  useEffect(() => {
    editId ? setLoading(hostAddonDataLoading) : setLoading(hostDataLoading);
  }, [hostAddonDataLoading, hostDataLoading, setLoading, editId]);

  useEffect(()=> {
    if (rates.length === 0) {
      handleAddRate();
    }
  }, [handleAddRate, rates.length]);

  useEffect(()=> {
    if (rates.length === 1 && isDeleted) {
      updateOptionInput(0, 'optionName', null);
      setIsDeleted(false);
    }
  }, [rates, isDeleted, updateOptionInput]);

  useEffect(() => {
    setTimeslotList([]);
  }, [currency])

  const updateSelectedTicketTypes = (addOnCustomizationList) => {
    const selectedTicketTypesClone = {};
    addOnCustomizationList.forEach(addOnCustomization => {
      const { rateId, listingId, capacityLimit } = addOnCustomization;
      if (capacityLimit === null) {
        if (selectedTicketTypesClone[listingId]) {
          selectedTicketTypesClone[listingId].add(rateId);
        }
        else selectedTicketTypesClone[listingId] = new Set([rateId]);
      }
    })
    setSelectedTicketTypes(selectedTicketTypesClone);
  }

  const addOnHostQueryResponse = (addOnData) => {
    if (addOnData) {
      const addOn = addOnData?.addOn;
      const currenciesData = addOnData?.host?.currencies?.map(cur => {
        return { text: cur, value: cur };
      });
      const hostTaxes = addOnData?.host?.taxes.nodes;
      const availableTax = addOn ? filterAvailableTaxes(addOn.taxes, hostTaxes) : hostTaxes;
      const selectedTax = addOn ? addOn?.taxes?.map(() => true) : [];
      let tax = addOn ? addOn.taxes : [];
      const forceHostPayCommission = addOnData?.host?.forceHostToPayCommission;
      let hostPays = hostPaysCommission;
      const isPostPurchaseMessageNullOrEmpty = () => 
        !(addOn.postPurchaseMessage === null || !getCharacterCountWithoutHTML(addOn.postPurchaseMessage));

      if (addOn && rates[0].id == null) {
        hostPays = addOn.hostPaysCommission; 
      }
  
      if (hostTaxes?.length === 0) {
        tax = [placeHolderTaxRate];
      }
  
      // If addOn then we are on the Edit page Else Create Page
      if (addOn) {
        const ratesAddon = addOnData?.addOn?.rates;
        const imagesAddon = ratesAddon.flatMap((rateVal) => rateVal.images);
        const imageUrlAddon = ratesAddon[0].imageUrl;
        setName(addOn.name);
        setDescription(addOn.description);
        setCurrency(addOn.currency);
        setCurrencies(currenciesData);
        setRates(ratesAddon);
        setImageUrl(imageUrlAddon);
        setImages(imagesAddon);
        setTaxes(tax);
        setSelectedTaxes(selectedTax);
        setAvailableTaxes(availableTax);
        setListingIds(addOn.eventIds);
        setForceHostToPayCommission(forceHostPayCommission);
        setHostPaysCommission(hostPays);
        updateSelectedTicketTypes(addOn.addOnCustomizations);
        setPostPurchaseMessage(addOn.postPurchaseMessage);
        setShowPostPurchaseMessage(isPostPurchaseMessageNullOrEmpty());
        setCapacityLimitCustomization(addOn.addOnCustomizations);
        setAddOnAvailabilityRule(addOn.addOnAvailabilityRules);
        setHideQRCode(addOn.suppressQrCode);
      }
      else {
        setTaxes(tax);
        setSelectedTaxes(selectedTax);
        setAvailableTaxes(availableTax);
        setCurrencies(currenciesData);
        setListingIds(listingIds);
        setForceHostToPayCommission(forceHostPayCommission);
        setHostPaysCommission(hostPays);
        currenciesData.length !== 0 && setCurrency(currenciesData[0]?.value);
      }
    }
    setData(addOnData);
  }

  useEffect(()=> {
    if (rates.length === 1 && isDeleted) {
      updateOptionInput(0, 'optionName', null);
      setIsDeleted(false);
    }    
  }, [rates, isDeleted, updateOptionInput]);

  const emptyTaxRate = {
    id: EMPTY_TAX_RATE_ID,
    name: '',
    percent: 0.0,
    taxId: ''
  };

  const updateOptionInput = useCallback((index, key, value) => {
    let updatedValue = value;

    if (value === '') {
      updatedValue = null;
    }

    if (key === 'price') {
      updatedValue = !Number.isNaN(parseFloat(value)) ? parseFloat(value) : null;
    }

    if (['maxQuantity', 'minQuantity'].includes(key)) {
      updatedValue = parseInt(value, 10);
    }

    const updatedOption = Object.assign({}, rates[index], {
      [key]: updatedValue
    });

    const newRates = [
      ...rates.slice(0, index),
      updatedOption,
      ...rates.slice(index + 1)
    ];
    setRates(newRates);
  }, [rates]);

  const handleAddRate = useCallback(()=> {
    const newRates = [...rates, emptyRate()];
    setRates(newRates);
  }, [rates]);

  const clearImage = () => {
    setAddImage(undefined);
    setImageUrl(null);
    setImages([]);
  }

  const emptyRate = () => {
    return {
      id: null,
      name: null,
      optionName: null,
      availability: null,
      price: null,
      state: 'ACTIVE',
      maxQuantity: null,
      minQuantity: null,
      sku: null
    };
  }

  const renderFlashErrorMessages = () => {
    return serverErrors.length > 0
      ? <ErrorFlash errors={props.intl.formatMessage(addons.errorFlashMessage)} detailed />
      : null;
  }

  const renderNameErrorMessage = () => {
    const errorName = clientErrors.name;
    if (errorName.characterLength) 
      return props.intl.formatMessage(addons.addOnNameLengthErrorMessage);
    else if (errorName.blank && name?.length === 0)
      return props.intl.formatMessage(addons.addOnNameBlankErrorMessage);
    return null;
  }

  const titleCharacterCount = (<label className={style.characterCounter}>
      {name.length}/{MAX_TITLE_LENGTH}
    </label>);
  const textCharacterCounter = (text) => (<label className={style.characterCounter}>
      {getCharacterCountWithoutHTML(text)}/{MAX_DESCRIPTION_LENGTH}
    </label>);

  const validateRemoveOption = (index) => {
    return !rates[index].used;
  }

  const handleRemoveOption = (index) => {
    if (validateRemoveOption(index)) {
      const newRates = [...rates.slice(0, index), ...rates.slice(index + 1)];
      setDeletedRateIndex(index);
      setRates(newRates);
      setIsDeleted(true);
    }
    else {
      setClientErrors(Object.assign({}, clientErrors, {rates: {[index.toString()]: { used: true }}}));
    }
  }

  const filterAvailableTaxes = (filterTaxes, hostTaxes) => {
    let availableTax = hostTaxes;
    if (filterTaxes.length > 0) {
      availableTax = differenceWith(availableTax, filterTaxes, isEqual);
    }
    return availableTax;
  }

  const clearErrors = () => {
    setServerErrors([]);
    setClientErrors({
      name: {
        blank: null,
        characterLength: null
      },
      price: {
        blank: null
      },
      description: {
        characterLength: null
      },
      optionNames: {
        blank: null
      },
      rates: {}
    });
  }

  const handleSelectTax = (index, newTaxId) => {
    const newAvailableTaxes = availableTaxes;
    const availableTaxIndex = newAvailableTaxes.findIndex(tax => tax.id === newTaxId);
    if (availableTaxIndex === -1) {
      return;
    }
    const newTaxes = [...taxes];
    const newSelectedTaxes = selectedTaxes;
    if (newTaxes[index].id !== EMPTY_TAX_RATE_ID) {
      newAvailableTaxes.push(newTaxes[index]);
    }
    const tax = newAvailableTaxes[availableTaxIndex];
    newTaxes[index] = tax;
    newSelectedTaxes[index] = true;
    setTaxes(newTaxes);
    setSelectedTaxes(newSelectedTaxes);
    setAvailableTaxes(newAvailableTaxes.filter((_, currentIndex) => currentIndex !== availableTaxIndex));
  }

  // This tax rate is used when the user clicks on 'Custom Tax Rate'
  // This indicates that we're creating a custom tax rate
  // If the current tax rate is a placeholder, don't insert into available taxes
  // if the current tax rate is the placeholder, show the custom tax rate form.
  const placeHolderTaxRate = {
      name: '',
      id: PLACEHOLDER_TAX_RATE_ID,
      percent: 0
    };

  const renderOptionNamesErrorMessage = () => {
    return clientErrors.optionNames.blank 
      ? <div className={`error__label ${style.errorLabel}`}>
          <Exclamation />
          <span>{props.intl.formatMessage(addons.optionNamesErrorMessage)}</span>
        </div>
      : undefined;
  }

  const renderFeeMessage = () => {
    if (forceHostToPayCommission) {
      return <div>
        {props.intl.formatMessage(addons.feeMessage)}
        <Tooltip top='-145px'>
          {props.intl.formatMessage(addons.feeSelectDisabled)}
        </Tooltip>
      </div>;
    }
    return props.intl.formatMessage(addons.feeMessage);
  }

  const handleAddTax = () => {
    selectedTaxes.push(false);
    const newTaxes = [...taxes, emptyTaxRate];
    setTaxes(newTaxes);
    setSelectedTaxes(selectedTaxes);
  }

  const handleCustomTaxSelection = (index) => {
    const newAvailableTaxes = availableTaxes;
    const newTaxes = [...taxes];
    if (selectedTaxes[index] && !FILTER_TAX_IDS.includes(newTaxes[index].id)) {
      newAvailableTaxes.push(newTaxes[index]);
    }
    newTaxes[index] = placeHolderTaxRate;
    setTaxes(newTaxes);
    setAvailableTaxes(newAvailableTaxes);
  }

  const handleRemoveTax = (index) => {
    const tax = taxes[index];
    const availableTax = [...availableTaxes];
    if (
      tax.id !== EMPTY_TAX_RATE_ID &&
      tax.id !== CUSTOM_TAX_SELECTION_ID &&
      tax.id !== PLACEHOLDER_TAX_RATE_ID
    ) {
      availableTax.push(tax);
    }

    setTaxes([...taxes.slice(0, index), ...taxes.slice(index + 1)]);
    setSelectedTaxes([...selectedTaxes.slice(0, index), ...selectedTaxes.slice(index + 1)]);
    setAvailableTaxes(availableTax);
  }

  const handleSaveTax = async (index) => {
    const tax = taxes[index];
    setSavingList(Object.assign({}, savingList, {
      [index]: true
    }));

    await createTax({
      variables: {
        name: tax.unformattedName,
        percent: tax.percent,
        taxId: tax.taxId,
        hostId:props.hostId
      }}).then((response) => {
        if (response.data?.createTax?.tax) {
          const newSelectedTaxes = selectedTaxes;
          const newTaxes = taxes;
          const createdTax = response.data.createTax.tax;
          newSelectedTaxes[index] = true;
          newTaxes[index] = createdTax;
          setSavingList(Object.assign({}, savingList, {[index]: false}));
          setTaxes(newTaxes);
          setSelectedTaxes(newSelectedTaxes);
        }
      }).catch(() => {
        setSavingList(Object.assign({}, savingList, {[index]: false}));
      }
    );
  }

  const updateTaxInput = (index, key, value) => {
    const updatedValue = key === 'percent' ? parseFloat(value) : value;
    const updatedTax = Object.assign({}, taxes[index], {
      [key]: updatedValue
    });
    const newTaxes = [
      ...taxes.slice(0, index),
      updatedTax,
      ...taxes.slice(index + 1)
    ];
    setTaxes(newTaxes);
  }

  const customTaxSelection = {
    id: CUSTOM_TAX_SELECTION_ID,
    label: props.intl.formatMessage(addons.customTaxSelection),
  };

  const availableTaxesList = () => {
    return [
      ...(availableTaxes || []),
      customTaxSelection
    ];
  }

  const setAddOnImage = (image)  => {
    if (image) {
      setAddImage(image);
      setImageUrl(image);
      setImages([{uploadId: image.uploadId}]);
    }
  }

  const scrollToBasicInfo = () => {
    elements.basicInfoHeader.scrollIntoView(true);
    window.scrollBy(0, -150);
  }

  const validateForm = () => {
    clearErrors();
    const clientErr = Object.assign({}, clientErrors);
    let rateErrors;

    // Check if name field is blank
    if (name.length === 0) {
      clientErr.name.blank = true;
      scrollToBasicInfo();
    }
    else {
      clientErr.name.blank = false;
    }

    // Check if name is greater than 95 characters
    if (name.length > 95) {
      clientErr.name.characterLength = true;
      scrollToBasicInfo();
    }
    else {
      clientErr.name.characterLength = false;
    }

    if (rates.length > 1) {
      rates.some((currentRate, index) => {
        if ([null, '', undefined].includes(currentRate.optionName)) {
          clientErr.rates = { [index.toString()]: { blankOptionName: true }};
          rateErrors = true;
          elements.rows[index].scrollIntoView(true);
          window.scrollBy(0, -150);
          return rateErrors;
        }
        return false;
      });
    }

    if (rates.length > 0) {
      rates.map((currentRate, index) => {
        const { maxQuantity, minQuantity } = currentRate;
        const minMaxArray = [maxQuantity, minQuantity];
        if (minMaxArray.includes(0) || (!minMaxArray.includes(null) && (maxQuantity < minQuantity))) {
          clientErr.rates = { ...clientErr.rates, [index.toString()]: { optionQuantity: true }};
          rateErrors = true;
          elements.rows[index].scrollIntoView(true);
          window.scrollBy(0, -150);
          return rateErrors;
        }

        if (currentRate.price > MAX_PRICE_ALLOWED) {
          clientErr.rates = { [`${index}`]: { optionPrice: true }};
          rateErrors = true;
          elements.rows[index].scrollIntoView(true);
          window.scrollBy(0, -150);
          return rateErrors;
        }

        if (currentRate.capacity > MAX_PRICE_ALLOWED) {
          clientErr.rates = { [`${index}`]: { optionCapacity: true }};
          rateErrors = true;
          elements.rows[index].scrollIntoView(true);
          window.scrollBy(0, -150);
          return rateErrors;
        }

        if ((currentRate.capacity !== null)
          && (currentRate.capacity < currentRate.capacities?.[0]?.count_attending)) {
          rateErrors = true;
          elements.rows[index].scrollIntoView(true);
          window.scrollBy(0, -150);
          return rateErrors;
        }
        return false;
      });

      addOnCapacity.forEach(capacity => {
        capacity.addOnRatesCapacity.forEach((addon) => {
          if (addon.capacityLimit === 0) {       
            rateErrors = true;
          }
          return rateErrors;
        });
        return false;
      });
    }

    const checkEditorError = (key, text) => {
      if (getCharacterCountWithoutHTML(text) > MAX_DESCRIPTION_LENGTH) {
        clientErr[key].characterLength = true;
        scrollToBasicInfo();
      }
      else {
        clientErr[key].characterLength = false;
      }
    }
    checkEditorError('description', description);
    setClientErrors(clientErr);

    const isValid = !clientErr.name.characterLength
      && !clientErr.price.blank
      && !clientErr.name.blank
      && !clientErr.description.characterLength
      && !rateErrors
      && !clientErr.optionNames.blank;

    return isValid;
  }

  const renderErrorMessages = (errors) => {
    setServerErrors(errors);
  }

  const shapeNewRates = (appliedRates, addOnName, isDraft = false) => {
    return appliedRates.map(
      ({ id, optionName, description: rateDescription, state, price, sku, capacity, maxQuantity, minQuantity }) => {
        const rateState = isDraft ? 'INACTIVE' : state;
        return {
          id,
          attributes: {
            name: addOnName,
            optionName,
            description: rateDescription,
            state: rateState,
            price,
            sku,
            capacity,
            maxQuantity,
            minQuantity,
          }
        };
      }
    ) ;
  }

  const shapeNewImages = (currentImages) => {
    return currentImages.map(({ uploadId }) => {
      return {
        uploadId,
      };
    });
  }

  const renderRequiredSymbol = (key) =>
    key ? <span className="custom-input-field--required"> *</span> : null;

  const renderAddOnOptions = (field, intl) => {
    return (rates.map((currentRate, index) => {
      const soldQuantity = currentRate.capacities?.[0]?.count_attending || 0;
      const hasRates = rates.length > 1;

      const tooltipStyle = (top) => {
        return {
          borderRadius: '5px',
          background: 'var(--color-ink)',
          padding: '8px 8px',
          maxWidth: '290px',
          fontSize: '1rem',
          top,
          left: '8px',
          zIndex: '1',
        }
      }

      const renderDeleteButtonWeb = (disabled) => {
        return (
          <div className={style.trashIconContainer}>
            <button
              className={`${style.optionTrashIcon} ui fluid icon button`}
              disabled={disabled}
              onClick={() => handleRemoveOption(index)}
            >
              <i aria-hidden="true" className="grey large icon universe-delete" />
            </button>
          </div>);
      }

      const trashIcon = currentRate?.used
        ? <Popup
            size="tiny"
            inverted
            on="hover"
            trigger= {renderDeleteButtonWeb(true)}
            style={tooltipStyle('55px')}
            position="top center"
            content={intl.formatMessage(addons.cannotDeleteSoldOutRate)}
          />
        : renderDeleteButtonWeb(false);

      const currentAddOnRate = clientErrors.rates[index];
      const hasOptionNameError = () => currentAddOnRate?.blankOptionName && currentRate.optionName === null;
      const addOnInputClasses = `${style.addOnInputs} ${hasOptionNameError() ? 'error' : ''}`;
      const nameInput = rates.length > 1
        ? <div className={style.optionNameContainer}>
          <Input
            id={field.nameInput.id}
            value={currentRate.optionName}
            onChange={(e) => {
              updateOptionInput(index, 'optionName', e.target.value);
            }}
            type={field.nameInput.type}
            placeholder={intl.formatMessage(addons.nameInputPlaceHolder)}
            className={addOnInputClasses}
            isError={hasOptionNameError()}
            errorMessage={intl.formatMessage(addons.optionNamesErrorMessage)}
          >
            {intl.formatMessage(addons.nameInput)} {index + 1}
              {renderRequiredSymbol(field.nameInput.required)}
              <Tooltip
                className={style.inputTooltipIcon}
                customIcon={<ToolTipIcon className={style.nameInputToolTipIcon} />}
                top="-55px"
                left="18px"
              >
                {intl.formatMessage(addons.printedTicketCharactersDisplayingLimit)}
              </Tooltip>
            </Input>
          </div>
        : undefined;

      const optionUsedErrors = currentAddOnRate?.used
        ? <div onClick={clearErrors}
            role='button'
            tabIndex={0}
            className={`error__label ${style.errorLabel}`}
          >
            <Exclamation />
            <span>{intl.formatMessage(addons.optionUsedErrorMessage)}</span>
          </div>
        : undefined;

      const optionPriceErrorMessage = currentAddOnRate?.optionPrice
        ? <div onClick={clearErrors}
            className={`error__label ${style.errorLabel}`}
            role='button'
            tabIndex={0}
          >
            <Exclamation />
            <span>{props.intl.formatMessage(addons.optionInvalidPriceErrorMessage)}</span>
          </div>
        : undefined;

      const optionQuantityErrorMessage = currentAddOnRate?.optionQuantity 
        ? <div className={`error__label ${style.errorLabel}`}>
            <Exclamation />
            <span>{props.intl.formatMessage(addons.optionQuantityErrorMessage)}</span>
          </div>
        : undefined;

      const optionCapacityErrorMessage = currentAddOnRate?.optionCapacity
        ? <div onClick={clearErrors}
            className={`error__label ${style.errorLabel}`}
            role='button'
            tabIndex={0}
          >
            <ErrorIcon />
            <span>{props.intl.formatMessage(addons.optionQuantityErrorMessage)}</span>
          </div>
        : undefined;

      const updateCapacity = (updatedIndex, quantityAvailableInput) => {
        const soldOut = rates[updatedIndex].capacities?.[0]?.count_attending;
        const formattedInput = quantityAvailableInput === '' ? null : parseInt(quantityAvailableInput, 10);
        let availableQuantity = formattedInput - soldOut;
        if (availableQuantity < 0) {
          availableQuantity = 0;
        }
        const updatedOption = Object.assign({}, rates[updatedIndex], {
          availability: soldOut !== undefined ? availableQuantity : formattedInput,
          capacity: formattedInput
        });
        const newRates = [...rates.slice(0, updatedIndex), updatedOption, ...rates.slice(updatedIndex + 1)];
        setRates(newRates);
      }

      const renderDeleteOption = () => {
        const rateDeleteClasses = `${style.rateDeleteOption} ${currentRate?.used && style.disableColor}`;

        return rates.length > 1
          ? (currentRate?.used
            ? <Popup
                size="tiny"
                inverted
                hoverable
                trigger={
                  <button className={rateDeleteClasses}>{props.intl.formatMessage(addons.deleteOption)} </button>
                }
                style={tooltipStyle('0px')}
                position="top center"
                content={intl.formatMessage(addons.cannotDeleteSoldOutRate)}
              />
          : <button
              className={rateDeleteClasses}
              onClick={() => handleRemoveOption(index)}
            >
              {props.intl.formatMessage(addons.deleteOption)}
            </button>)
        : <></>
      }

      const priceInputClasses = `${style.addOnInputs} ${clientErrors.price.blank ? 'error' : ''}`;
      const skuInputClasses = `${style.skuColumn} ${rates.length > 1 ? '' : style.singleRate}`;
      const quantityInputClasses = `${style.quantityColumn} ${rates.length > 1 ? '' : style.singleRate}`;
      const dividingLineClasses = `${style.line} ${currencies?.length === 1 ? style.hidden : ''}`;
      const currencySymbolText = currency ? `(${getSymbolFromCurrency(currency)} ${currency})` : '';
      const capacityInputAvailable = `${style.addOnInputs} ${currentRate.capacity !== null &&
        (currentRate.availability === 0 || currentRate.capacity < soldQuantity) ? 'error' : ''}`;
      const capacityInputSoldOut = (currentRate.availability === 0
        || currentRate.capacity < soldQuantity) ? style.soldOutColor : ''

      return (
        <>
          <div className={dividingLineClasses} />
          <div className={style.addOnRatesContainer}>
            <div key={currentRate.id || index} ref={r => { elements.rows[index] = r }} className={style.addOnRates}>
              <div className={`${style.rowWrapper} ${style.optionWrapper} ${style.gridWrapper}`}>
                {nameInput}
                <Input
                  id={field.priceInput.id}
                  value={currentRate.price}
                  onChange={e => {
                    updateOptionInput(index, 'price', e.target.value);
                  }}
                  type={field.priceInput.type}
                  placeholder={intl.formatMessage(addons.priceInputPlaceHolder)}
                  className={priceInputClasses}
                  required={field.priceInput.required}
                  isError={clientErrors.price.blank}
                  errorMessage={intl.formatMessage(addons.priceInputErrorMessage)}
                  min="0"
                  onKeyDown={blockInvalidChar}
                >
                  {`${intl.formatMessage(addons.priceInput)} ${currencySymbolText}`}
                </Input>
                <div className={style.optionNameContainer}>
                  <Input
                    id={field.capacityInput.id}
                    value={currentRate.capacity}
                    onChange={(e) => {
                      updateCapacity(index, e.target.value);
                    }}
                    type={field.capacityInput.type}
                    placeholder={currentRate.capacity || intl.formatMessage(addons.capacityInputPlaceholder)}
                    className={capacityInputAvailable}
                    required={field.capacityInput.required}
                    min="0"
                    onKeyDown={blockInvalidChar}
                  >
                    {intl.formatMessage(addons.capacityInput)}
                  </Input>
                  <div className={style.capacityAvailable}>
                    {(currentRate.capacity !== undefined
                      && currentRate.capacity !== null)
                      && <span className={capacityInputSoldOut}>
                        {`${intl.formatMessage(addons.available)} ${currentRate.availability}/${currentRate.capacity},
                          ${intl.formatMessage(addons.sold)} ${soldQuantity}/${currentRate.capacity}`}
                      </span>
                    }
                    {currentRate.capacity === null
                      && <span>
                        {`${intl.formatMessage(addons.available)}`} <span>&#8734;</span>
                        {`, ${intl.formatMessage(addons.sold)} ${soldQuantity}/`}<span>&#8734;</span>
                      </span>}
                  </div>
                </div>
              </div>
              <div className={`${style.rowWrapper} ${style.optionWrapper} ${style.gridWrapper}`}>
                <div className={skuInputClasses}>
                  <Input
                    id={field.skuInput.id}
                    value={currentRate.sku || ''}
                    onChange={(e) => {
                      updateOptionInput(index, 'sku', e.target.value);
                    }}
                    type={field.skuInput.type}
                    placeholder={intl.formatMessage(addons.skuInputPlaceholder)}
                    className={style.addOnInputs}
                    required={field.skuInput.required}
                  >
                    {intl.formatMessage(addons.skuInput)}
                  </Input>
                </div>
                <div className={quantityInputClasses}>
                  <div>{intl.formatMessage(addons.minMaxQuantityInput)}</div>
                  <div className={style.minQuantityInput}>
                    <Input
                      id={field.minQuantityInput.id}
                      value={currentRate.minQuantity}
                      onChange={(e) => {
                        updateOptionInput(index, 'minQuantity', e.target.value);
                      }}
                      type={field.minQuantityInput.type}
                      placeholder={intl.formatMessage(addons.minQuantityInputPlaceholder)}
                      className={`${style.addOnInputs} ${style.minQuantityInput}`}
                      required={field.minQuantityInput.required}
                      min="0"
                      onKeyDown={blockInvalidChar}
                    />
                  </div>
                  <div className={style.maxQuantityInput}>
                    <Input
                      id={field.maxQuantityInput.id}
                      value={currentRate.maxQuantity}
                      onChange={(e) => {
                        updateOptionInput(index, 'maxQuantity', e.target.value);
                      }}
                      type={field.maxQuantityInput.type}
                      className={`${style.addOnInputs} ${style.maxQuantityInput}`}
                      required={field.maxQuantityInput.required}
                      placeholder={intl.formatMessage(addons.maxQuantityInputPlaceholder)}
                      min="0"
                      onKeyDown={blockInvalidChar}
                    />
                  </div>
                </div>
              </div>
              {optionUsedErrors}
              {optionQuantityErrorMessage}
              {optionPriceErrorMessage}
              {optionCapacityErrorMessage}
            </div>
            <MediaQuery minWidth={SMALL_DEVICE_BREAK_POINT + 1}>
              {hasRates ? trashIcon : <></>}
            </MediaQuery>
            <MediaQuery maxWidth={SMALL_DEVICE_BREAK_POINT}>
              {renderDeleteOption()}
            </MediaQuery>
          </div>
        </>
      );
    }));
  }

  const renderAddOnImage = () => {
    return <div className={style.addOnImageSection}>
      <div className={style.basicInfoHeader}>
        {props.intl.formatMessage(addons.photo)}
      </div>
      <div className={style.imageUploadContainer}>
        <div className={style.addonsPhoto}>
          <label className={style.descriptiveText}>
            {props.intl.formatMessage(addons.uploadBody)}
          </label>
          {(imageUrl && !addImage)
            ? <div className={style.uploadedPhotoContainer}>
                <img src={imageUrl} className={style.imageTrashOverlay} alt="trash" />
                <DeleteIcon 
                  className={style.imageTrashIcon}
                  onClick={() => clearImage()}
                  role='button' tabIndex={0} 
                />
              </div>
            : <ImageUpload
                id="add-on-image-upload"
                name="add-on-image-upload"
                index={0}
                canCrop
                addButton={props.intl.formatMessage(addons.addPhoto)}
                changeButton={props.intl.formatMessage(addons.changePhoto)}
                handleUpload={(image) => setAddOnImage(image)}
                clearImage={clearImage}
                image={addImage}
              />
          }
        </div>
      </div>
    </div>
  }

  const prepareAddOnCustomization = () => {
    const addOnCustomizationPayload = [];
    if (Object.keys(selectedTicketTypes).length) {
      if (addOnCapacity.length === 0 || toggle === INACTIVE) {
        Object.keys(selectedTicketTypes).forEach(eventId => {
          if (selectedTicketTypes[eventId]) {
            addOnCustomizationPayload.push({
              eventId,
              ticketTypeIds: Array.from(selectedTicketTypes[eventId]),
            });
          } else {
            addOnCustomizationPayload.push({
              eventId,
              ticketTypeIds: [],
            })
          }
        });
      }
      else {
        Object.keys(selectedTicketTypes).forEach(id => {
          let addOnLimit = [];
          const addOnLimitData = addOnCapacity.find(addon => addon.eventId === id);
          const addOnLimitNotFount = addOnCapacity.filter(addon => addon.addOnRatesCapacity.length === 0);

          if (addOnLimitData?.eventId && addOnLimitData?.addOnRatesCapacity.length) {
            const ticketTypeIds = selectedTicketTypes[id] ? Array.from(selectedTicketTypes[id]) : [];
            addOnLimit = addOnLimitData.addOnRatesCapacity;
            addOnCustomizationPayload.push({
              eventId: id,
              ticketTypeIds,
              addOnRatesCapacity: addOnLimit
            });
          }
          else {
            selectedTicketTypes[id] && addOnCustomizationPayload.push({
              eventId: id,
              ticketTypeIds: Array.from(selectedTicketTypes[id]),
            });
          }

          if (addOnLimitNotFount.length > 0) {
            addOnLimitNotFount.forEach((cl => {
              const limitExist = addOnCustomizationPayload.find(addonC => addonC.eventId === cl.eventId)
              if (!limitExist) {
                addOnCustomizationPayload.push(cl);
              }
            }));
          }
        });
      }
    }
    return addOnCustomizationPayload.length
      ? addOnCustomizationPayload
      : ((toggle === ACTIVE) && (addOnCapacity.length) ? addOnCapacity : null);
  }

  const prepareAvailabilityRules = () => {
    const availabilityRules = [];
    let filterTimeslotList = [];
    if (listingIds.length > 0) {
      filterTimeslotList = timeslotList.filter(timeslot => listingIds.includes(timeslot.listingId));
    } else {
      filterTimeslotList = timeslotList;
    }
    filterTimeslotList.forEach(slot => {
      const startTimeObj = slot.startTime && moment(slot.startTime, 'hh:mm A');
      const endTimeObj = slot.endTime && moment(slot.endTime, 'hh:mm A');
      availabilityRules.push({
        addOnAvailabilityRuleId: slot.addOnAvailabilityRuleId || null,
        listingId: slot.listingId,
        startDate: slot.startDate ? slot.startDate.format('YYYY-MM-DD') : null,
        endDate: slot.endDate ? slot.endDate.format('YYYY-MM-DD') : null,
        startMinute: startTimeObj ? startTimeObj.minutes() : null,
        endMinute: endTimeObj ? endTimeObj.minutes() : null,
        startHour: startTimeObj ? startTimeObj.hour() : null,
        endHour: endTimeObj ? endTimeObj.hour() : null,
        weekdays: slot.weekdays.length > 0 ? slot.weekdays.toString() : null,
      });
    });
    return availabilityRules.length ? availabilityRules.reverse() : [];
  }

  const renderButtonSelector = (renderCancel) => {
    if (!props.addOnId) {
      return <CreateButtons
        name={name}
        rates={rates}
        images={images}
        taxes={taxes}
        listingIds={listingIds}
        description={description}
        hostPaysCommission={hostPaysCommission}
        currency={currency}
        intl={props.intl}
        validateForm={validateForm}
        hostId={props.hostId}
        renderErrorMessages={renderErrorMessages}
        prepareAddOnCustomization={prepareAddOnCustomization}
        postPurchaseMessage={postPurchaseMessage}
        renderCancel={renderCancel}
        sanitizeEditorText={sanitizeEditorText}
        history={props.history}
        shapeNewRates={shapeNewRates}
        shapeNewImages={shapeNewImages}
        prepareAvailabilityRules={prepareAvailabilityRules}
        hideQRCode={hideQRCode}
      />
    }
    return <EditButton
      taxes={taxes}
      name={name}
      rates={rates}
      images={images}
      listingIds={listingIds}
      description={description}
      hostPaysCommission={hostPaysCommission}
      currency={currency}
      intl={props.intl}
      validateForm={validateForm}
      addOnId={editId}
      hostId={props.hostId}
      renderErrorMessages={renderErrorMessages}
      prepareAddOnCustomization={prepareAddOnCustomization}
      postPurchaseMessage={postPurchaseMessage}
      sanitizeEditorText={sanitizeEditorText}
      history={props.history}
      shapeNewRates={shapeNewRates}
      shapeNewImages={shapeNewImages}
      renderCancel={renderCancel}
      prepareAvailabilityRules={prepareAvailabilityRules}
      hideQRCode={hideQRCode}
    />
  }
  const hasClientNameError = () => name?.length === 0 && (clientErrors.name.blank || clientErrors.name.characterLength);
  const addNameInputClasses = `${style.addOnInputs} ${hasClientNameError()
    ? 'error'
    : ''}`;
  const currencyContainerClasses = `${style.outerContainer} ${currencies?.length === 1 ? style.hidden : ''}`;

  const isEditPage = () => {
    const currentRoute = props.location.pathname;
    return matchPath(currentRoute, {
      path: '/dashboard/add-ons/edit/:id',
      exact: false,
      strict: false,
    });
  }

  const updateListingIds = newListingIds => setListingIds(newListingIds);

  const renderListingsSelector = () => {
    return <ListingsSelector
      updateListingIds={updateListingIds}
      singleCurrency={currencies?.length === 1}
      selectedCurrency={currency}
      loading={loading}
      intl={props.intl}
      listingIds={listingIds}
      hostId={props.hostId}
      addOnId={editId}
      isEditPage={isEditPage()}
      selectedTicketTypes={selectedTicketTypes}
      setSelectedTicketTypes={setSelectedTicketTypes}
      setEventData={setEventData}
      setDataLoader={setDataLoader}
      listingsFilterSetting={listingsFilterSetting}
      setListingsFilterSetting={setListingsFilterSetting}
    />;
  }

  const renderFeesAndTaxes = () => {
    let isFeeAbsorbedIntoPrice = false;
    if (forceHostToPayCommission || hostPaysCommission || hostPaysCommission === null) {
      isFeeAbsorbedIntoPrice = true;
    }
    const feeOptions = [
      { value: true, text: props.intl.formatMessage(addons.absorbFees) },
      { value: false, text: props.intl.formatMessage(addons.passFeesToBuyer) }
    ];
    const absorbFeesButtonClasses = `${style.button}
      ${isFeeAbsorbedIntoPrice ? style.active : ''}`;
    const passFeesButtonClasses = `${style.button}
      ${!isFeeAbsorbedIntoPrice ? style.active : ''}`;

    return <section className={style.addOnFeeAndTaxSection}>
      <div className={style.addOnFeeSection}>
        <h3 className={style.basicInfoHeader}>
          {props.intl.formatMessage(addons.feesAndTaxes)}
        </h3>
        <div className={style.feeMessageAndSelector}>
          <div className={style.feeMessage}>
            {renderFeeMessage()}
          </div>
          <div className={style.feeSelector}>
            <div className={style.container}>
              <Responsive minWidth={breakpoints.largestMobile + 1}>
                <div className={style.buttonBar}>
                  <div
                    className={absorbFeesButtonClasses}
                    onClick={() => setHostPaysCommission(feeOptions[0].value)}
                    role='button'
                    tabIndex={0}
                  >
                    {feeOptions[0].text}
                  </div>
                  <div className={style.buttonDivider} />
                  <div
                    className={passFeesButtonClasses}
                    onClick={() => setHostPaysCommission(feeOptions[1].value)}
                    role='button'
                    tabIndex={0}
                  >
                    {feeOptions[1].text}
                  </div>
                </div>
              </Responsive>
              <Responsive maxWidth={breakpoints.largestMobile}>
                <Dropdown
                  id={fields.addOnHostPaysCommission.id}
                  onChange={(_, { value }) => {
                    setHostPaysCommission(value);
                  }}
                  selection
                  options={feeOptions}
                  value={isFeeAbsorbedIntoPrice}
                  className={style.selector}
                  disabled={forceHostToPayCommission}
                />
              </Responsive>
            </div>
          </div>
        </div>
      </div>
      <div className={style.addOnTaxSection}>
        <div className={style.addOnTaxInfo}>
          <div className={style.addOnTaxHeader}>
            {props.intl.formatMessage(addons.taxes)}
          </div>
          <div className={style.taxMessage}>
            {props.intl.formatMessage(addons.taxMessage)}
          </div>
        </div>
        <TaxForm
          savingList={savingList}
          selectedTaxes={selectedTaxes}
          taxes={taxes}
          availableTaxes={availableTaxes}
          handleAddTax={handleAddTax}
          handleCustomTaxSelection={handleCustomTaxSelection}
          handleSelectTax={handleSelectTax}
          handleRemoveTax={handleRemoveTax}
          handleSave={handleSaveTax}
          updateTaxInput={updateTaxInput}
          availableTaxesList={availableTaxesList}
          {...props}
        />
      </div>
    </section>
  }

  const onPostPurchaseOptionClicked = () => {
    if (showPostPurchaseMessage) setPostPurchaseMessage('');
    setShowPostPurchaseMessage(!showPostPurchaseMessage);
  }

  const sanitizeEditorText = (text) => {
    if (getCharacterCountWithoutHTML(text) === 0) return '';
    return text;
  }

  const renderPostPurchaseMessage = () => {
    const { intl: { formatMessage }} = props;
    return <div
      className={`${style.postPurchaseMessage} ${showPostPurchaseMessage ? style.postPurchaseMessageVisible : ''}`}
    >
      {showPostPurchaseMessage
        ? <div>
            <div className={style.postPurchaseMessageHeader}>{formatMessage(addons.postPurchaseMessageHeader)}</div>
            <div className={style.postPurchaseMessageSubheader}>
              {formatMessage(addons.postPurchaseMessageSubheader)}
            </div>
            <PostPurchaseMessageInput
              id={fields.addOnPostPurchaseMessage.id}
              description={postPurchaseMessage}
              setDescription={setPostPurchaseMessage}
              showVideo={false}
              showImage={false}
              showHeading={false}
            />
          </div>
        : <></>
      }
      <div
        onClick={onPostPurchaseOptionClicked}
        role="button"
        tabIndex={0}
        className={`${style.postPurchaseMessageState} ${showPostPurchaseMessage ? style.postPurchaseMessageShown : ''}`}
      > 
        {showPostPurchaseMessage 
          ? <><BlueMinusSign /> {formatMessage(addons.hidePostPurchaseMessage)}</>
          : <><Icon className="universe-plus" /> {formatMessage(addons.showPostPurchaseMessage)}</>
        }
      </div>
    </div>
  }

  const renderAddOnMessage = () => {
    return <div className={style.addOnPageDescriptionMessage}>
      {props.intl.formatMessage(addons.addOnsDescriptions)}
    </div>
  }

  const renderCancelButton = () => {
    return <Button 
      className={`${style.cancelLink} Button default-type small`} 
      onClick={() => props.setCancelModalOpen(true)}
    >
      <FormattedMessage
        {...addons.cancel}
      />
    </Button>;
  }

  const showEditorError = (text) => getCharacterCountWithoutHTML(text) > MAX_DESCRIPTION_LENGTH;

  const updateLimitCapacity = newCapacity => setAddOnCapacity(newCapacity);

  const renderAddOnsCapacity = () => {
    return <AddOnsCapacity
      rates={rates}
      name={name}
      eventData={eventData}
      selectedTicketTypes={selectedTicketTypes}
      updateLimitCapacity={updateLimitCapacity}
      selectedCurrency={currency}
      capacityLimitCustomization={capacityLimitCustomization}
      listingIds={listingIds}
      deletedRateIndex={deletedRateIndex}
      setDeletedRateIndex={setDeletedRateIndex}
      setToggle={setToggle}
      toggle={toggle}
      dataLoader={dataLoader}
    />
  }

  const renderListingTimeslot = () => {
    return <ListingTimeslot
        eventData={eventData}
        timeslotList={timeslotList}
        setTimeslotList={setTimeslotList}
        listingIds={listingIds}
        dataLoader={dataLoader}
        addOnAvailabilityRule={addOnAvailabilityRule}
        selectedTicketTypes={selectedTicketTypes}
        listingsFilterSetting={listingsFilterSetting}
      />
  }

  return (
    <div className={style.content}>
      <div className={style.addOnsTitle}>
        {props.headerTitle}
        {renderAddOnMessage()}
        {renderFlashErrorMessages()}
      </div> 
      <section className={`${style.form} ${style.basicInfoSection}`}>
        <h3 className={style.basicInfoHeader} ref={r => { elements.basicInfoHeader = r }}>
          {props.intl.formatMessage(addons.basicInformation)}
        </h3>
        <Input
          id={fields.addOnName.id}
          value={name}
          onChange={e => {
            setName(e.target.value);
          }}
          type={fields.addOnName.type}
          placeholder={props.intl.formatMessage(addons.addOnNamePlaceholder)}
          className={addNameInputClasses}
          maxLength={MAX_TITLE_LENGTH}
          isError={hasClientNameError()}
          errorMessage={renderNameErrorMessage()}
        >
          <span className={style.addOnNameHeader}>
            {props.intl.formatMessage(addons.addOnName)}
            {renderRequiredSymbol(fields.addOnName.required)}
            <span className={style.nameLengthCounter}>
              {titleCharacterCount}
            </span>
          </span>
        </Input>
        <div className={style.addOnNameSubheading}>
          <ToolTipIcon /> {props.intl.formatMessage(addons.printedTicketCharactersDisplayingLimit)}
        </div>
        <div className={style.addOnDescriptionMessage}>
          {props.intl.formatMessage(addons.describeAddOn)}
          <br />
          <div className={style.descriptionSubheading}>
            {props.intl.formatMessage(addons.describeAddOnSubheading)}
            {textCharacterCounter(description)}
          </div>
          {fields.addOnDescription.required ? <span className="custom-input-field--required">*</span> : null}
          {Object.keys(data).length
            ? <AddOnDescriptionInput
              id={fields.addOnDescription.id}
              placeholder={props.intl.formatMessage(addons.describeAddOnPlaceholder)}
              description={description}
              setDescription={setDescription}
              showVideo={false}
              showImage={false}
              showHeading={false}
              error={showEditorError(description)}
            />
            : <></>
          }
          {clientErrors.description.characterLength && showEditorError(description)
            ? <div className={style.addOnDescriptionError}>
                <Exclamation /> {props.intl.formatMessage(addons.addOnDescriptionLengthErrorMessage)}
              </div>
            : ''
          }
        </div>
        {renderPostPurchaseMessage()}
      </section>
      <section className={style.addOnDetailsSection}>
        <h3 className={style.basicInfoHeader}>{props.intl.formatMessage(addons.addOnDetails)}</h3>
        <div className={currencyContainerClasses}>
          <div className={style.container}>
          <div className={style.currencyHeader}>{props.intl.formatMessage(addons.selectCurrency)}</div>
            {
              data &&
              <Dropdown
                id={fields.selectCurrency.id}
                onChange={(_, { value }) => {
                  setCurrency(value);
                  setListingIds([]);
                }}
                selection
                options={currencies}
                value={currency}
                className={style.selector}
              />
            }
          </div>
        </div>
        <div className={style.toggleRow}>
          <Checkbox
            toggle
            checked={hideQRCode}
            onChange={(_, { checked }) => setHideQRCode(checked)}
          />
          <span>
            {props.intl.formatMessage(addons.hideQrCode)}
          </span>
        </div>
        <div className={style.qrHeading}>
          {props.intl.formatMessage(addons.qrCodeHeading)}
        </div>
        <div className={style.line} />
        {renderAddOnImage()}
        {renderAddOnOptions(fields, props.intl)}
        {renderOptionNamesErrorMessage()}
        <div className={style.line} />
        <div className={`${style.gridWrapper} ${style.addOptionRow}`}>
          <Button
            className={`white ${style.addNewOptionBtn} ui fluid inverted primary button`}
            onClick={handleAddRate}
          >
            <Icon className="universe-plus" />
            {props.intl.formatMessage(addons.addOption)}
          </Button>
        </div>
      </section>
      {renderFeesAndTaxes()}
      {currency && (
        <>
          {renderListingsSelector()}
          {renderListingTimeslot()}
          {renderAddOnsCapacity()}
        </>)
      }
      <footer className={style.footer}>
        <Responsive minWidth={breakpoints.largestMobile + 1}>
          <div className={style.buttonContainer}>
            {renderCancelButton()}
            {renderButtonSelector()}
          </div>
        </Responsive>
        <Responsive maxWidth={breakpoints.largestMobile}>
          <div className={style.buttonContainer}>
            {renderButtonSelector(renderCancelButton)}
          </div>
        </Responsive>
      </footer>
    </div>
  );
});

export default AddOnsCreateEditForm;
